import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Botao,
  CampoDeTexto,
  DialogoDeConfirmacao,
  EstadoDeErro,
  Esqueleto,
  cn,
} from "@base/ui";
import { Button, Modal } from "@base/ui/renke";
import type { MetaPaginaDisponivel } from "@base/contracts";
import { CircleCheck, QrCode, RefreshCw, Search, Unplug } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LogoX } from "../../../shared/branding/logo-x";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { abrirOauthEmPopup } from "../../../shared/auth/oauth-popup";
import { useProjetoAtivo } from "../../../shared/tenant/projeto.contexto";
import {
  parsePaletaHex,
  validarSomaVolumetria,
} from "./validar-formulario-cliente";
import { useClientesGateway } from "../application/clientes.contexto";
import { ModalLoginQrTikTok } from "./modal-login-tiktok";
import {
  CabecalhoDasRedes,
  CartaoDeRede,
  ContaDaRede,
  RecadoDaRede,
  RedeVazia,
  type TomDaRede,
} from "./redes-do-projeto";
import {
  LogoFacebook,
  LogoInstagram,
  LogoTikTok,
} from "../../../shared/branding/logos-redes";
import {
  useAtualizarBrand,
  useAtualizarCliente,
  useAtualizarDossie,
  useAtualizarVolumetria,
  useBrandGuidelines,
  useClienteAtual,
  useCriarData,
  useDatasComemorativas,
  useDossie,
  useMetaConexao,
  useContasSociais,
  useRemoverData,
  useRestaurarVersaoDossie,
  useSalvarVersaoDossie,
  useTemGerirClientes,
  useVersoesDossie,
  useVolumetria,
} from "../application/use-clientes";

type SecaoDoProjeto =
  | "perfil"
  | "brand"
  | "datas"
  | "volumetria"
  | "dossie"
  | "meta"
  | "excluir";

type ItemDeSecao = {
  readonly id: SecaoDoProjeto;
  readonly rotulo: string;
  readonly resumo: string;
};

type GrupoDeSecao = {
  readonly rotulo: string;
  readonly itens: readonly ItemDeSecao[];
};

const GRUPOS_PROJETO: readonly GrupoDeSecao[] = [
  {
    rotulo: "Identidade",
    itens: [
      {
        id: "perfil",
        rotulo: "Perfil",
        resumo: "Nome, tom e posicionamento",
      },
      {
        id: "brand",
        rotulo: "Brand",
        resumo: "Paleta, fontes e tom visual",
      },
    ],
  },
  {
    rotulo: "Planejamento",
    itens: [
      {
        id: "datas",
        rotulo: "Datas",
        resumo: "Comemorativas e marcos",
      },
      {
        id: "volumetria",
        rotulo: "Volumetria",
        resumo: "Meta mensal por formato",
      },
    ],
  },
  {
    rotulo: "Contexto",
    itens: [
      {
        id: "dossie",
        rotulo: "Dossiê",
        resumo: "Texto longo e versões",
      },
    ],
  },
  {
    rotulo: "Publicação",
    itens: [
      {
        id: "meta",
        rotulo: "Redes do cliente",
        resumo: "Facebook, Instagram, X e TikTok",
      },
    ],
  },
  {
    rotulo: "Avançado",
    itens: [
      {
        id: "excluir",
        rotulo: "Excluir projeto",
        resumo: "Desativa este workspace",
      },
    ],
  },
];

const SECOES_VALIDAS = new Set<string>(
  GRUPOS_PROJETO.flatMap((g) => g.itens.map((i) => i.id)),
);

function lerSecaoDoHash(): SecaoDoProjeto {
  const query = window.location.hash.split("?")[1] ?? "";
  const secao = new URLSearchParams(query).get("secao");
  if (secao && SECOES_VALIDAS.has(secao)) {
    return secao as SecaoDoProjeto;
  }
  return "perfil";
}

function escreverSecaoNoHash(secao: SecaoDoProjeto): void {
  window.location.hash = `configuracoes?secao=${secao}`;
}

const NOMES_MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
] as const;

function CartaoSecao({
  titulo,
  descricao,
  acao,
  children,
  perigo = false,
}: {
  readonly titulo: string;
  readonly descricao?: string;
  readonly acao?: ReactNode;
  readonly children: ReactNode;
  readonly perigo?: boolean;
}) {
  return (
    <Card
      flat
      className={
        perigo
          ? "gap-0 border-destructive/30 py-0 shadow-none"
          : "gap-0 py-0 shadow-none"
      }
    >
      <CardHeader className="border-b border-border/50 px-5 py-5 sm:px-6 sm:py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <CardTitle
              className={
                perigo
                  ? "text-base font-semibold tracking-tight text-destructive"
                  : "text-base font-semibold tracking-tight"
              }
            >
              {titulo}
            </CardTitle>
            {descricao ? (
              <CardDescription className="text-sm leading-relaxed">
                {descricao}
              </CardDescription>
            ) : null}
          </div>
          {acao}
        </div>
      </CardHeader>
      <CardContent className="px-5 py-6 sm:px-6 sm:py-7">{children}</CardContent>
    </Card>
  );
}

/**
 * Workspace ativo: perfil, brand, datas, volumetria, dossie e Meta.
 * Subcategorias (uma secao por vez) em Configuracoes > Projeto.
 * Redes do cliente (publicacao) ficam em secao=meta; Instagram de
 * monitoramento fica em Configuracoes > Sua conta.
 */
export function ConteudoDoProjeto() {
  const podeGerir = useTemGerirClientes();
  const [secao, definirSecao] = useState<SecaoDoProjeto>(() => lerSecaoDoHash());

  useEffect(() => {
    const sync = () => definirSecao(lerSecaoDoHash());
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  useEffect(() => {
    if (secao === "excluir" && !podeGerir) {
      definirSecao("perfil");
      escreverSecaoNoHash("perfil");
    }
  }, [podeGerir, secao]);

  const grupos = useMemo(
    () =>
      GRUPOS_PROJETO.map((grupo) => ({
        ...grupo,
        itens: grupo.itens.filter(
          (item) => item.id !== "excluir" || podeGerir,
        ),
      })).filter((grupo) => grupo.itens.length > 0),
    [podeGerir],
  );

  const itemAtivo = useMemo(
    () =>
      grupos.flatMap((g) => g.itens).find((item) => item.id === secao) ??
      grupos[0]?.itens[0],
    [grupos, secao],
  );

  function selecionarSecao(id: SecaoDoProjeto) {
    definirSecao(id);
    escreverSecaoNoHash(id);
  }

  return (
    <div className="space-y-6 pb-4">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
          Contexto do projeto
        </h2>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
          Escolha uma categoria à esquerda e edite com foco. Cada bloco salva
          sozinho.
        </p>
      </header>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
        <nav
          aria-label="Categorias do projeto"
          className="flex shrink-0 gap-4 overflow-x-auto pb-1 lg:w-48 lg:flex-col lg:gap-5 lg:overflow-visible lg:pb-0"
        >
          {grupos.map((grupo) => (
            <div key={grupo.rotulo} className="min-w-0 shrink-0 space-y-1.5">
              <p className="px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {grupo.rotulo}
              </p>
              <div className="flex gap-1 lg:flex-col">
                {grupo.itens.map((item) => {
                  const ativo = secao === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => selecionarSecao(item.id)}
                      aria-current={ativo ? "page" : undefined}
                      className={cn(
                        "flex min-w-[8.5rem] cursor-pointer flex-col rounded-lg px-2.5 py-2 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-w-0 lg:w-full",
                        ativo
                          ? "bg-accent text-accent-foreground"
                          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                        item.id === "excluir" && !ativo
                          ? "hover:text-destructive"
                          : null,
                        item.id === "excluir" && ativo
                          ? "bg-destructive/10 text-destructive"
                          : null,
                      )}
                    >
                      <span className="text-sm font-medium tracking-tight">
                        {item.rotulo}
                      </span>
                      <span
                        className={cn(
                          "mt-0.5 hidden text-xs leading-snug lg:block",
                          ativo
                            ? "text-accent-foreground/70"
                            : "text-muted-foreground",
                          item.id === "excluir" && ativo
                            ? "text-destructive/80"
                            : null,
                        )}
                      >
                        {item.resumo}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="min-w-0 flex-1 space-y-3">
          {itemAtivo ? (
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground lg:hidden">
              {itemAtivo.rotulo}
            </p>
          ) : null}
          {secao === "perfil" ? <SecaoPerfil /> : null}
          {secao === "brand" ? <SecaoBrand /> : null}
          {secao === "datas" ? <SecaoDatas /> : null}
          {secao === "volumetria" ? <SecaoVolumetria /> : null}
          {secao === "dossie" ? <SecaoDossie /> : null}
          {secao === "meta" ? <SecaoMeta /> : null}
          {secao === "excluir" && podeGerir ? <SecaoExcluirProjeto /> : null}
        </div>
      </div>
    </div>
  );
}


/**
 * @deprecated Use Configuracoes > Projeto. Mantido so como redirecionamento.
 */
export function PaginaDeCliente() {
  useEffect(() => {
    window.location.hash = "configuracoes";
  }, []);
  return null;
}

function SecaoPerfil() {
  const cliente = useClienteAtual();
  const salvar = useAtualizarCliente();
  const [nome, definirNome] = useState("");
  const [tomDeVoz, definirTomDeVoz] = useState("");
  const [posicionamento, definirPosicionamento] = useState("");
  const [preferenciasVisuais, definirPreferenciasVisuais] = useState("");
  const [notas, definirNotas] = useState("");
  const [hidratado, definirHidratado] = useState(false);
  const [ok, definirOk] = useState<string | null>(null);

  useEffect(() => {
    if (!cliente.data || hidratado) return;
    definirNome(cliente.data.nome);
    definirTomDeVoz(cliente.data.tomDeVoz ?? "");
    definirPosicionamento(cliente.data.posicionamento ?? "");
    definirPreferenciasVisuais(cliente.data.preferenciasVisuais ?? "");
    definirNotas(cliente.data.notas ?? "");
    definirHidratado(true);
  }, [cliente.data, hidratado]);

  async function aoSalvar(e: FormEvent) {
    e.preventDefault();
    definirOk(null);
    await salvar.mutateAsync({
      nome,
      tomDeVoz: tomDeVoz || null,
      posicionamento: posicionamento || null,
      preferenciasVisuais: preferenciasVisuais || null,
      notas: notas || null,
    });
    definirOk("Perfil salvo.");
  }

  return (
    <CartaoSecao
      titulo="Perfil"
      descricao="Nome, tom e posicionamento usados em briefings e geração assistida."
    >
      {cliente.isPending && !hidratado ? (
        <Esqueleto className="h-24 w-full" />
      ) : null}
      {cliente.isError ? (
        <EstadoDeErro
          mensagem={
            cliente.error instanceof ErroDaApi
              ? cliente.error.message
              : "Não foi possível carregar o cliente."
          }
          aoTentarNovamente={() => void cliente.refetch()}
        />
      ) : null}
      {hidratado ? (
        <form className="space-y-4" onSubmit={(e) => void aoSalvar(e)}>
          <CampoDeTexto
            rotulo="Nome"
            value={nome}
            onChange={(e) => definirNome(e.target.value)}
            required
          />
          <CampoDeTexto
            rotulo="Tom de voz"
            value={tomDeVoz}
            onChange={(e) => definirTomDeVoz(e.target.value)}
          />
          <CampoDeTexto
            rotulo="Posicionamento"
            value={posicionamento}
            onChange={(e) => definirPosicionamento(e.target.value)}
          />
          <CampoDeTexto
            rotulo="Preferências visuais"
            value={preferenciasVisuais}
            onChange={(e) => definirPreferenciasVisuais(e.target.value)}
          />
          <CampoDeTexto
            rotulo="Notas"
            value={notas}
            onChange={(e) => definirNotas(e.target.value)}
          />
          {salvar.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {salvar.error instanceof Error
                ? salvar.error.message
                : "Falha ao salvar."}
            </p>
          ) : null}
          {ok ? (
            <p className="text-sm text-muted-foreground" role="status">
              {ok}
            </p>
          ) : null}
          <Botao
            type="submit"
            carregando={salvar.isPending}
            disabled={nome.trim().length < 2 || salvar.isPending}
          >
            Salvar perfil
          </Botao>
        </form>
      ) : null}
    </CartaoSecao>
  );
}

const ID_RENKE_STUDIO = "a0000000-0000-4000-8000-000000000001";

function SecaoExcluirProjeto() {
  const podeGerir = useTemGerirClientes();
  const { projetos, projetoAtivoId, excluirProjetoAtivo } = useProjetoAtivo();
  const [excluindo, definirExcluindo] = useState(false);
  const [confirmando, definirConfirmando] = useState(false);
  const nome =
    projetos.find((p) => p.id === projetoAtivoId)?.nome ?? "este projeto";
  const ehRenkeStudio = projetoAtivoId === ID_RENKE_STUDIO;
  const unicoProjeto = projetos.length <= 1;
  const bloqueado = ehRenkeStudio || unicoProjeto;

  if (!podeGerir) return null;

  return (
    <CartaoSecao
      titulo="Excluir projeto"
      perigo
      descricao={
        ehRenkeStudio
          ? "O projeto da conta (Renke Studio) não pode ser excluído."
          : unicoProjeto
            ? "Não dá para excluir o único projeto da conta. Crie outro antes."
            : "O projeto some da lista (fica inativo). Os dados ficam isolados; os outros projetos não mudam."
      }
    >
      <Botao
        type="button"
        variante="perigo"
        disabled={bloqueado || excluindo}
        onClick={() => definirConfirmando(true)}
      >
        Excluir projeto
      </Botao>
      <DialogoDeConfirmacao
        aberto={confirmando}
        titulo={`Excluir "${nome}"?`}
        descricao="O projeto some da lista (fica inativo). Os dados dele ficam isolados; os outros projetos não mudam."
        rotuloConfirmar="Excluir projeto"
        tomConfirmar="perigo"
        carregando={excluindo}
        aoCancelar={() => {
          if (!excluindo) definirConfirmando(false);
        }}
        aoConfirmar={() => {
          definirExcluindo(true);
          void excluirProjetoAtivo().finally(() => {
            definirExcluindo(false);
            definirConfirmando(false);
          });
        }}
      />
    </CartaoSecao>
  );
}

function SecaoBrand() {
  const brand = useBrandGuidelines();
  const salvar = useAtualizarBrand();
  const [tom, definirTom] = useState("");
  const [publico, definirPublico] = useState("");
  const [paleta, definirPaleta] = useState("");
  const [termos, definirTermos] = useState("");
  const [tags, definirTags] = useState("");
  const [fontes, definirFontes] = useState("");
  const [referencias, definirReferencias] = useState("");
  const [ok, definirOk] = useState<string | null>(null);
  const [erroLocal, definirErroLocal] = useState<string | null>(null);

  useEffect(() => {
    if (!brand.data) return;
    definirTom(brand.data.tomDeVoz ?? "");
    definirPublico(brand.data.publicoAlvo ?? "");
    definirPaleta(brand.data.paletaCores.join(", "));
    definirTermos(brand.data.termosProibidos.join(", "));
    definirTags(brand.data.tagsTom.join(", "));
    definirFontes(brand.data.fontes.join(", "));
    definirReferencias(brand.data.referenciasVisuais.join(", "));
  }, [brand.data]);

  async function aoSalvar(e: FormEvent) {
    e.preventDefault();
    definirOk(null);
    definirErroLocal(null);
    const paletaParseada = parsePaletaHex(paleta);
    if (!paletaParseada.ok) {
      definirErroLocal(paletaParseada.erro);
      return;
    }
    const refs = referencias
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean);
    await salvar.mutateAsync({
      tomDeVoz: tom || null,
      publicoAlvo: publico || null,
      paletaCores: [...paletaParseada.cores],
      termosProibidos: termos
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      tagsTom: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      fontes: fontes
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean),
      referenciasVisuais: refs,
    });
    definirOk("Brand guidelines salvas.");
  }

  return (
    <CartaoSecao
      titulo="Brand guidelines"
      descricao="Paleta em hex e listas separadas por vírgula. Guia visual e de tom para o time."
    >
      {brand.isPending ? <Esqueleto className="h-20 w-full" /> : null}
      <form className="space-y-4" onSubmit={(e) => void aoSalvar(e)}>
        <CampoDeTexto
          rotulo="Tom de voz"
          value={tom}
          onChange={(e) => definirTom(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Público-alvo"
          value={publico}
          onChange={(e) => definirPublico(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Paleta (hex)"
          value={paleta}
          onChange={(e) => definirPaleta(e.target.value)}
          placeholder="Ex.: # seguido de 6 digitos hex"
        />
        <CampoDeTexto
          rotulo="Termos proibidos"
          value={termos}
          onChange={(e) => definirTermos(e.target.value)}
          placeholder="barato, promoção"
        />
        <CampoDeTexto
          rotulo="Tags de tom"
          value={tags}
          onChange={(e) => definirTags(e.target.value)}
          placeholder="acolhedor, direto"
        />
        <CampoDeTexto
          rotulo="Fontes"
          value={fontes}
          onChange={(e) => definirFontes(e.target.value)}
          placeholder="Inter, SF Pro"
        />
        <CampoDeTexto
          rotulo="Referências visuais (URLs)"
          value={referencias}
          onChange={(e) => definirReferencias(e.target.value)}
          placeholder="https://..."
        />
        {erroLocal ? (
          <p className="text-sm text-destructive" role="alert">
            {erroLocal}
          </p>
        ) : null}
        {salvar.isError && !erroLocal ? (
          <p className="text-sm text-destructive" role="alert">
            {salvar.error instanceof Error
              ? salvar.error.message
              : "Falha ao salvar brand."}
          </p>
        ) : null}
        {ok ? (
          <p className="text-sm text-muted-foreground" role="status">
            {ok}
          </p>
        ) : null}
        <Botao type="submit" carregando={salvar.isPending}>
          Salvar brand
        </Botao>
      </form>
    </CartaoSecao>
  );
}

function SecaoDatas() {
  const [mes, definirMes] = useState(() => new Date().getMonth() + 1);
  const datas = useDatasComemorativas(mes);
  const criar = useCriarData();
  const remover = useRemoverData();
  const [nomeEvento, definirNome] = useState("");
  const [dia, definirDia] = useState(1);

  async function aoCriar(e: FormEvent) {
    e.preventDefault();
    await criar.mutateAsync({
      nomeEvento,
      dia,
      mes,
      querComemorar: true,
      recorrenteAnual: true,
    });
    definirNome("");
  }

  return (
    <CartaoSecao
      titulo="Datas comemorativas"
      descricao="Marcos do calendário editorial deste cliente."
    >
      <div className="mb-4 space-y-2">
        <Label htmlFor="filtro-mes-datas">Mês</Label>
        <Select
          value={String(mes)}
          onValueChange={(valor) => definirMes(Number(valor))}
        >
          <SelectTrigger
            id="filtro-mes-datas"
            className="w-full max-w-xs cursor-pointer"
          >
            <SelectValue placeholder="Escolha o mês" />
          </SelectTrigger>
          <SelectContent>
            {NOMES_MESES.map((nome, indice) => (
              <SelectItem key={nome} value={String(indice + 1)}>
                {nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <ul className="space-y-2">
        {(datas.data ?? []).map((d) => (
          <li
            key={d.id}
            className="flex items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2.5 text-sm"
          >
            <span>
              {String(d.dia).padStart(2, "0")}/{String(d.mes).padStart(2, "0")}{" "}
              {d.nomeEvento}
              {!d.querComemorar ? " (sem comemoração)" : ""}
            </span>
            <button
              type="button"
              className="cursor-pointer text-destructive transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => void remover.mutateAsync(d.id)}
            >
              Remover
            </button>
          </li>
        ))}
        {(datas.data ?? []).length === 0 && !datas.isPending ? (
          <li className="rounded-lg border border-dashed border-border px-3 py-3 text-sm text-muted-foreground">
            Nenhuma data neste mês. Adicione a primeira.
          </li>
        ) : null}
      </ul>
      <form
        className="mt-4 flex flex-wrap items-end gap-2 border-t border-border/50 pt-4"
        onSubmit={(e) => void aoCriar(e)}
      >
        <CampoDeTexto
          rotulo="Dia"
          type="number"
          min={1}
          max={31}
          value={String(dia)}
          onChange={(e) => definirDia(Number(e.target.value) || 1)}
          className="w-20"
        />
        <CampoDeTexto
          rotulo="Evento"
          value={nomeEvento}
          onChange={(e) => definirNome(e.target.value)}
          required
        />
        <Botao type="submit" carregando={criar.isPending}>
          Adicionar
        </Botao>
      </form>
    </CartaoSecao>
  );
}

function SecaoVolumetria() {
  const vol = useVolumetria();
  const salvar = useAtualizarVolumetria();
  const [total, definirTotal] = useState(0);
  const [reels, definirReels] = useState(0);
  const [carrossel, definirCarrossel] = useState(0);
  const [estatico, definirEstatico] = useState(0);
  const [stories, definirStories] = useState(0);
  const [erroLocal, definirErroLocal] = useState<string | null>(null);

  const soma = reels + carrossel + estatico + stories;
  const erroSoma =
    erroLocal ??
    (soma !== total
      ? validarSomaVolumetria(total, { reels, carrossel, estatico, stories })
      : null);

  useEffect(() => {
    if (!vol.data) return;
    definirTotal(vol.data.totalPostsMes);
    definirReels(vol.data.distribuicao.reels);
    definirCarrossel(vol.data.distribuicao.carrossel);
    definirEstatico(vol.data.distribuicao.estatico);
    definirStories(vol.data.distribuicao.stories);
  }, [vol.data]);

  async function aoSalvar(e: FormEvent) {
    e.preventDefault();
    definirErroLocal(null);
    const distribuicao = { reels, carrossel, estatico, stories };
    const invalido = validarSomaVolumetria(total, distribuicao);
    if (invalido) {
      definirErroLocal(invalido);
      return;
    }
    await salvar.mutateAsync({
      totalPostsMes: total,
      distribuicao,
    });
  }

  return (
    <CartaoSecao
      titulo="Volumetria padrão"
      descricao={`A soma dos formatos precisa bater com o total mensal. Agora: ${soma} de ${total}.`}
    >
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => void aoSalvar(e)}
      >
        <CampoDeTexto
          rotulo="Total / mês"
          type="number"
          min={0}
          value={String(total)}
          onChange={(e) => definirTotal(Number(e.target.value) || 0)}
        />
        <CampoDeTexto
          rotulo="Reels"
          type="number"
          min={0}
          value={String(reels)}
          onChange={(e) => definirReels(Number(e.target.value) || 0)}
        />
        <CampoDeTexto
          rotulo="Carrossel"
          type="number"
          min={0}
          value={String(carrossel)}
          onChange={(e) => definirCarrossel(Number(e.target.value) || 0)}
        />
        <CampoDeTexto
          rotulo="Estático"
          type="number"
          min={0}
          value={String(estatico)}
          onChange={(e) => definirEstatico(Number(e.target.value) || 0)}
        />
        <CampoDeTexto
          rotulo="Stories"
          type="number"
          min={0}
          value={String(stories)}
          onChange={(e) => definirStories(Number(e.target.value) || 0)}
        />
        {erroSoma ? (
          <p className="text-sm text-destructive sm:col-span-2" role="alert">
            {erroSoma}
          </p>
        ) : null}
        {salvar.isError && !erroSoma ? (
          <p className="text-sm text-destructive sm:col-span-2" role="alert">
            {salvar.error instanceof Error
              ? salvar.error.message
              : "Distribuição inválida."}
          </p>
        ) : null}
        <div className="sm:col-span-2">
          <Botao
            type="submit"
            carregando={salvar.isPending}
            disabled={erroSoma !== null && erroSoma.length > 0}
          >
            Salvar volumetria
          </Botao>
        </div>
      </form>
    </CartaoSecao>
  );
}

function SecaoDossie() {
  const dossie = useDossie();
  const salvar = useAtualizarDossie();
  const versoes = useVersoesDossie();
  const snapshot = useSalvarVersaoDossie();
  const restaurar = useRestaurarVersaoDossie();
  const [status, definirStatus] = useState<"idle" | "salvando" | "salvo">(
    "idle",
  );

  const editor = useEditor({
    extensions: [StarterKit],
    content: "",
    editorProps: {
      attributes: {
        class:
          "min-h-40 rounded-lg border border-border/70 bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring prose prose-sm max-w-none",
      },
    },
  });

  useEffect(() => {
    if (!editor || !dossie.data) return;
    const md = dossie.data.conteudoMarkdown ?? "";
    if (editor.getText() === "" && md) {
      editor.commands.setContent(md);
    }
  }, [editor, dossie.data]);

  const persistir = useCallback(async () => {
    if (!editor) return;
    definirStatus("salvando");
    const markdown = editor.getText();
    await salvar.mutateAsync({
      conteudoJson: editor.getJSON(),
      conteudoMarkdown: markdown,
    });
    definirStatus("salvo");
  }, [editor, salvar]);

  useEffect(() => {
    if (!editor) return;
    let timer: number | undefined;
    const onUpdate = () => {
      definirStatus("idle");
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void persistir();
      }, 5_000);
    };
    editor.on("update", onUpdate);
    return () => {
      editor.off("update", onUpdate);
      window.clearTimeout(timer);
    };
  }, [editor, persistir]);

  const statusRotulo =
    status === "salvando"
      ? "Salvando…"
      : status === "salvo"
        ? "Salvo"
        : "Salva sozinho após 5s parado";

  return (
    <CartaoSecao
      titulo="Dossiê"
      descricao="Contexto longo do cliente. Autosave após 5 segundos parado."
      acao={
        <Badge variant="secondary" className="tabular-nums">
          {statusRotulo}
        </Badge>
      }
    >
      <EditorContent editor={editor} />
      <div className="mt-4 flex flex-wrap gap-2">
        <Botao
          type="button"
          variante="secundario"
          onClick={() => void persistir()}
        >
          Salvar agora
        </Botao>
        <Botao
          type="button"
          variante="fantasma"
          carregando={snapshot.isPending}
          onClick={() => void snapshot.mutateAsync()}
        >
          Salvar versão
        </Botao>
      </div>
      {(versoes.data ?? []).length > 0 ? (
        <ul className="mt-4 space-y-2 border-t border-border/50 pt-4">
          {(versoes.data ?? []).map((v) => (
            <li
              key={v.id}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <span className="text-muted-foreground">
                {new Date(v.criadoEm).toLocaleString("pt-BR")} · {v.autorSujeito}
              </span>
              <button
                type="button"
                className="cursor-pointer font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => void restaurar.mutateAsync(v.id)}
              >
                Restaurar
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </CartaoSecao>
  );
}

function SecaoMeta() {
  const meta = useMetaConexao();
  const sociais = useContasSociais();
  const gateway = useClientesGateway();
  const podeGerir = useTemGerirClientes();
  const [erro, definirErro] = useState<string | null>(null);
  const [avisoOauth, definirAvisoOauth] = useState<string | null>(null);
  const [modalPaginasAberto, definirModalPaginasAberto] = useState(false);
  const [paginas, definirPaginas] = useState<readonly MetaPaginaDisponivel[]>(
    [],
  );
  const [paginaEscolhida, definirPaginaEscolhida] = useState<string>("");
  const [carregandoPaginas, definirCarregandoPaginas] = useState(false);
  const [salvandoPagina, definirSalvandoPagina] = useState(false);
  const [buscaPagina, definirBuscaPagina] = useState("");
  const [modoVinculoEscolhido, definirModoVinculoEscolhido] = useState<
    "instagram" | "pagina" | "pagina_e_instagram"
  >("instagram");
  const jaAbriuSelecaoRef = useRef(false);
  const [modalQrAberto, definirModalQrAberto] = useState(false);

  async function abrirSelecaoPaginas() {
    definirErro(null);
    definirBuscaPagina("");
    definirModoVinculoEscolhido("instagram");
    definirCarregandoPaginas(true);
    definirModalPaginasAberto(true);
    jaAbriuSelecaoRef.current = true;
    try {
      const itens = await gateway.listarPaginasMeta();
      definirPaginas(itens);
      const atual = meta.data?.conexao?.facebookPageId;
      const inicial =
        (atual && itens.some((p) => p.facebookPageId === atual)
          ? atual
          : itens[0]?.facebookPageId) ?? "";
      definirPaginaEscolhida(inicial);
      if (itens.length === 0) {
        definirErro(
          "Nenhuma página apareceu nesta conta do Facebook. Confira se você liberou as páginas certas na autorização.",
        );
      }
    } catch (causa) {
      definirErro(
        causa instanceof Error
          ? causa.message
          : "Não foi possível listar as páginas. Tente reconectar.",
      );
      definirModalPaginasAberto(false);
    } finally {
      definirCarregandoPaginas(false);
    }
  }

  async function tratarResultadoOauth(oauth: string) {
    if (oauth.endsWith("_ok")) {
      if (oauth.startsWith("meta")) {
        definirAvisoOauth(
          "Autorização concluída. Agora escolha a página e o Instagram deste projeto.",
        );
        const atualizado = await meta.refetch();
        await sociais.refetch();
        if (
          atualizado.data?.conexao?.precisaSelecionarPagina ||
          !atualizado.data?.conexao?.facebookPageId
        ) {
          void abrirSelecaoPaginas();
        }
        return;
      }
      definirAvisoOauth("Conta conectada com sucesso.");
      await sociais.refetch();
      return;
    }
    if (oauth.endsWith("_erro")) {
      definirErro("Não deu para concluir a conexão. Tente de novo.");
    }
  }

  async function atualizarAposPopupSemMensagem() {
    const atualizado = await meta.refetch();
    await sociais.refetch();
    const conexao = atualizado.data?.conexao;
    if (!conexao || conexao.status === "nao_conectado") return;
    if (conexao.precisaSelecionarPagina || !conexao.facebookPageId) {
      definirAvisoOauth(
        "Autorização concluída. Agora escolha a página e o Instagram deste projeto.",
      );
      void abrirSelecaoPaginas();
      return;
    }
    definirAvisoOauth("Conta conectada. Os dados foram atualizados.");
  }

  useEffect(() => {
    const query = window.location.hash.split("?")[1] ?? "";
    const oauth = new URLSearchParams(query).get("oauth");
    if (!oauth) return;
    // Popup: o App ja notificou o opener e fecha; aqui so fallback mesma aba.
    if (window.opener && !window.opener.closed) return;
    if (window.name === "renke-oauth") return;
    const params = new URLSearchParams(query);
    params.delete("oauth");
    const resto = params.toString();
    window.location.hash = resto
      ? `configuracoes?${resto}`
      : "configuracoes?secao=meta";
    void tratarResultadoOauth(oauth);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- oauth callback
  }, []);

  useEffect(() => {
    if (
      meta.data?.conexao?.precisaSelecionarPagina &&
      podeGerir &&
      !modalPaginasAberto &&
      !jaAbriuSelecaoRef.current
    ) {
      void abrirSelecaoPaginas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- abre uma vez quando faltar pagina
  }, [meta.data?.conexao?.precisaSelecionarPagina, podeGerir]);

  async function vincularMeta() {
    definirErro(null);
    try {
      const { url } = await gateway.iniciarMetaOAuth();
      const resultado = await abrirOauthEmPopup(url);
      if (resultado) {
        await tratarResultadoOauth(resultado.oauth);
        return;
      }
      await atualizarAposPopupSemMensagem();
    } catch (causa) {
      definirErro(
        causa instanceof Error
          ? causa.message
          : "A conexão com Facebook e Instagram ainda não está disponível neste ambiente.",
      );
    }
  }

  async function desvincularMeta() {
    definirErro(null);
    try {
      await gateway.desvincularMeta();
      await meta.refetch();
      await sociais.refetch();
    } catch (causa) {
      definirErro(
        causa instanceof Error
          ? causa.message
          : "Não foi possível desconectar.",
      );
    }
  }

  async function confirmarPagina() {
    if (!paginaEscolhida) return;
    definirSalvandoPagina(true);
    definirErro(null);
    try {
      await gateway.selecionarPaginaMeta(
        paginaEscolhida,
        modoVinculoEscolhido,
      );
      definirModalPaginasAberto(false);
      definirBuscaPagina("");
      definirAvisoOauth(
        modoVinculoEscolhido === "instagram"
          ? "Pronto. Neste projeto vamos usar só o Instagram."
          : modoVinculoEscolhido === "pagina"
            ? "Pronto. Neste projeto vamos usar a página do Facebook."
            : "Pronto. Página e Instagram definidos para este projeto.",
      );
      await meta.refetch();
      await sociais.refetch();
    } catch (causa) {
      definirErro(
        causa instanceof Error
          ? causa.message
          : "Não foi possível salvar a escolha.",
      );
    } finally {
      definirSalvandoPagina(false);
    }
  }

  async function limparPagina() {
    definirErro(null);
    try {
      await gateway.limparPaginaMeta();
      definirAvisoOauth(
        "Página e Instagram desvinculados deste projeto. A autorização no Facebook continua; você pode escolher de novo quando quiser.",
      );
      await meta.refetch();
      await sociais.refetch();
    } catch (causa) {
      definirErro(
        causa instanceof Error
          ? causa.message
          : "Não foi possível remover o vínculo.",
      );
    }
  }

  async function vincularTwitter() {
    definirErro(null);
    try {
      const { url } = await gateway.iniciarTwitterOAuth();
      const resultado = await abrirOauthEmPopup(url);
      if (resultado) {
        await tratarResultadoOauth(resultado.oauth);
        return;
      }
      await sociais.refetch();
    } catch (causa) {
      definirErro(
        causa instanceof Error
          ? causa.message
          : "A conexão com o X ainda não está disponível neste ambiente.",
      );
    }
  }

  async function aoConectarTikTokPorQr(username: string) {
    definirModalQrAberto(false);
    definirErro(null);
    const apelido = username.trim().replace(/^@/, "");
    definirAvisoOauth(`Conta @${apelido} conectada.`);
    await sociais.refetch();
  }

  async function desvincularRede(plataforma: "twitter" | "tiktok") {
    definirErro(null);
    try {
      await gateway.desvincularContaSocial(plataforma);
      await sociais.refetch();
    } catch (causa) {
      definirErro(
        causa instanceof Error
          ? causa.message
          : "Não foi possível desconectar.",
      );
    }
  }

  const conexao = meta.data?.conexao;
  const health = meta.data?.health;
  const conectadaMeta = Boolean(conexao) && conexao?.status !== "nao_conectado";
  const paginaDefinida = Boolean(conexao?.facebookPageId);
  const contaTwitter = sociais.data?.contas.find(
    (c) => c.plataforma === "twitter",
  );
  const contaTikTok = sociais.data?.contas.find(
    (c) => c.plataforma === "tiktok",
  );
  const twitterOk = contaTwitter?.status === "conectado";
  const tiktokOk = contaTikTok?.status === "conectado";
  const paginaSelecionada = paginas.find(
    (p) => p.facebookPageId === paginaEscolhida,
  );
  const termoBusca = normalizarParaBusca(buscaPagina);
  const paginasParaModo =
    modoVinculoEscolhido === "instagram"
      ? paginas.filter((p) => Boolean(p.instagramPageId))
      : paginas;
  const paginasFiltradas = termoBusca
    ? paginasParaModo.filter((pagina) => {
        const alvo = normalizarParaBusca(
          `${pagina.nome} ${pagina.instagramUsername ?? ""}`,
        );
        return alvo.includes(termoBusca);
      })
    : paginasParaModo;
  const modoAtual = conexao?.modoVinculo;
  const mostrarPaginaFacebook =
    paginaDefinida &&
    (modoAtual === "pagina" || modoAtual === "pagina_e_instagram");
  const mostrarInstagram =
    paginaDefinida &&
    Boolean(conexao?.instagramPageId) &&
    (modoAtual === "instagram" ||
      modoAtual === "pagina_e_instagram" ||
      modoAtual == null);
  const badgeMeta =
    conexao?.status === "expirado"
      ? "Expirada"
      : conectadaMeta && paginaDefinida
        ? modoAtual === "instagram"
          ? "Só Instagram"
          : modoAtual === "pagina"
            ? "Só página"
            : "Conectada"
        : conectadaMeta
          ? "Falta escolher"
          : "Não conectada";

  const conectadas =
    (mostrarInstagram ? 1 : 0) +
    (mostrarPaginaFacebook ? 1 : 0) +
    (twitterOk ? 1 : 0) +
    (tiktokOk ? 1 : 0);
  const tomMeta: TomDaRede =
    conexao?.status === "expirado"
      ? "erro"
      : conectadaMeta && paginaDefinida
        ? "conectada"
        : conectadaMeta
          ? "atencao"
          : "desligada";
  const semPermissao = (
    <p className="text-sm leading-relaxed text-muted-foreground">
      Só quem administra o projeto conecta ou desconecta.
    </p>
  );
  const perfilTikTok = contaTikTok?.perfil ?? null;

  return (
    <div className="space-y-6">
      <CabecalhoDasRedes conectadas={conectadas} total={4} />

      {avisoOauth ? (
        <div
          className="flex items-start gap-3 rounded-xl border border-success-line bg-success-soft px-4 py-3 text-sm leading-relaxed text-success-soft-foreground"
          role="status"
        >
          <CircleCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {avisoOauth}
        </div>
      ) : null}
      {erro ? (
        <p
          className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm leading-relaxed text-destructive"
          role="alert"
        >
          {erro}
        </p>
      ) : null}

      {/* Duas colunas só quando o conteúdo tem largura, não a janela. */}
      <div className="@container">
        <div className="grid gap-4 @2xl:grid-cols-2">
          <CartaoDeRede
            className="@2xl:col-span-2"
            logo={
              <>
                <LogoInstagram className="size-4" />
                <LogoFacebook className="size-4" />
              </>
            }
            nome="Instagram e Facebook"
            descricao="Instagram profissional e página do Facebook deste projeto."
            tom={tomMeta}
            rotuloDoStatus={badgeMeta}
            acoes={
              podeGerir ? (
                <>
                  {conectadaMeta ? (
                    <Button
                      type="button"
                      variante="brand"
                      tamanho="sm"
                      className="cursor-pointer"
                      onClick={() => void abrirSelecaoPaginas()}
                    >
                      {paginaDefinida
                        ? "Trocar página ou Instagram"
                        : "Escolher página e Instagram"}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variante={conectadaMeta ? "secondary" : "brand"}
                    tamanho="sm"
                    className="cursor-pointer"
                    onClick={() => void vincularMeta()}
                  >
                    {conectadaMeta ? (
                      <>
                        <RefreshCw aria-hidden="true" />
                        Reconectar
                      </>
                    ) : (
                      "Conectar Instagram e Facebook"
                    )}
                  </Button>
                  <span className="flex-1" />
                  {conectadaMeta && paginaDefinida ? (
                    <Button
                      type="button"
                      variante="ghost"
                      tamanho="sm"
                      className="cursor-pointer"
                      onClick={() => void limparPagina()}
                    >
                      Remover página e Instagram
                    </Button>
                  ) : null}
                  {conectadaMeta ? (
                    <Button
                      type="button"
                      variante="ghost"
                      tamanho="sm"
                      className="cursor-pointer"
                      onClick={() => void desvincularMeta()}
                    >
                      <Unplug aria-hidden="true" />
                      Desconectar
                    </Button>
                  ) : null}
                </>
              ) : (
                semPermissao
              )
            }
          >
            {meta.isPending ? <Esqueleto className="h-20 w-full rounded-xl" /> : null}
            {conexao && conectadaMeta ? (
              paginaDefinida ? (
                mostrarPaginaFacebook || mostrarInstagram ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {mostrarInstagram ? (
                      <ContaDaRede
                        fotoUrl={conexao.instagramFotoUrl}
                        iniciais="IG"
                        selo={<LogoInstagram />}
                        nome={
                          conexao.instagramUsername
                            ? `@${conexao.instagramUsername}`
                            : "Conta profissional"
                        }
                        detalhe="Instagram profissional"
                      />
                    ) : null}
                    {mostrarPaginaFacebook ? (
                      <ContaDaRede
                        fotoUrl={conexao.facebookPageFotoUrl}
                        iniciais="FB"
                        selo={<LogoFacebook />}
                        nome={conexao.facebookPageNome?.trim() || "Página selecionada"}
                        detalhe="Página do Facebook"
                      />
                    ) : null}
                  </div>
                ) : (
                  <RecadoDaRede tom="atencao">
                    Há um vínculo técnico, mas nada aparece para publicar ainda.
                    Escolha de novo a página ou o Instagram.
                  </RecadoDaRede>
                )
              ) : (
                <RecadoDaRede tom="atencao">
                  A autorização no Facebook funcionou. Falta escolher o Instagram,
                  a página ou os dois.
                </RecadoDaRede>
              )
            ) : !meta.isPending ? (
              <RedeVazia>
                Autorize a conta do Facebook para escolher a página e o Instagram
                que a agenda vai usar.
              </RedeVazia>
            ) : null}
            {conexao?.status === "expirado" ? (
              <RecadoDaRede tom="erro">
                A autorização expirou. Reconecte para voltar a publicar.
              </RecadoDaRede>
            ) : null}
            {health?.status === "connected" &&
            health.expiresInDays != null &&
            health.expiresInDays <= 14 ? (
              <RecadoDaRede tom="atencao">
                A autorização vale por mais {health.expiresInDays}{" "}
                {health.expiresInDays === 1 ? "dia" : "dias"}. Vale reconectar
                antes que expire.
              </RecadoDaRede>
            ) : null}
          </CartaoDeRede>

          <CartaoDeRede
            logo={<LogoX className="size-4" />}
            nome="X"
            descricao="Texto e mídia publicados pela conta do cliente."
            tom={twitterOk ? "conectada" : "desligada"}
            rotuloDoStatus={twitterOk ? "Conectada" : "Não conectada"}
            acoes={
              podeGerir ? (
                <>
                  <Button
                    type="button"
                    variante={twitterOk ? "secondary" : "primary"}
                    tamanho="sm"
                    className="cursor-pointer"
                    onClick={() => void vincularTwitter()}
                  >
                    {twitterOk ? (
                      <>
                        <RefreshCw aria-hidden="true" />
                        Reconectar
                      </>
                    ) : (
                      "Conectar X"
                    )}
                  </Button>
                  <span className="flex-1" />
                  {twitterOk ? (
                    <Button
                      type="button"
                      variante="ghost"
                      tamanho="sm"
                      className="cursor-pointer"
                      onClick={() => void desvincularRede("twitter")}
                    >
                      <Unplug aria-hidden="true" />
                      Desconectar
                    </Button>
                  ) : null}
                </>
              ) : (
                semPermissao
              )
            }
          >
            {sociais.isPending ? <Esqueleto className="h-20 w-full rounded-xl" /> : null}
            {contaTwitter && twitterOk ? (
              <ContaDaRede
                fotoUrl={contaTwitter.fotoUrl}
                iniciais="X"
                selo={<LogoX />}
                nome={rotuloContaSocial(contaTwitter)}
                detalhe={contaTwitter.nomeExibicao?.trim() || "Conta do X"}
              />
            ) : !sociais.isPending ? (
              <RedeVazia>A agenda publica texto e mídia por essa conta.</RedeVazia>
            ) : null}
          </CartaoDeRede>

          <CartaoDeRede
            logo={<LogoTikTok className="size-4" />}
            nome="TikTok"
            descricao="Vídeos publicados pelo TikTok Studio da conta."
            tom={tiktokOk ? "conectada" : "desligada"}
            rotuloDoStatus={tiktokOk ? "Conectada" : "Não conectada"}
            acoes={
              podeGerir ? (
                <>
                  <Button
                    type="button"
                    variante={tiktokOk ? "secondary" : "primary"}
                    tamanho="sm"
                    className="cursor-pointer"
                    onClick={() => {
                      definirErro(null);
                      definirModalQrAberto(true);
                    }}
                  >
                    <QrCode aria-hidden="true" />
                    {tiktokOk ? "Entrar de novo" : "Entrar com QR code"}
                  </Button>
                  <span className="flex-1" />
                  {tiktokOk ? (
                    <Button
                      type="button"
                      variante="ghost"
                      tamanho="sm"
                      className="cursor-pointer"
                      onClick={() => void desvincularRede("tiktok")}
                    >
                      <Unplug aria-hidden="true" />
                      Desconectar
                    </Button>
                  ) : null}
                </>
              ) : (
                semPermissao
              )
            }
          >
            {sociais.isPending ? <Esqueleto className="h-20 w-full rounded-xl" /> : null}
            {contaTikTok && tiktokOk ? (
              <ContaDaRede
                fotoUrl={contaTikTok.fotoUrl}
                iniciais="TT"
                selo={<LogoTikTok />}
                nome={contaTikTok.nomeExibicao?.trim() || rotuloContaSocial(contaTikTok)}
                detalhe={
                  contaTikTok.username
                    ? `@${contaTikTok.username.replace(/^@/, "")}`
                    : "Conta do TikTok"
                }
                verificado={perfilTikTok?.verificado ?? false}
                numeros={[
                  { rotulo: "Seguidores", valor: perfilTikTok?.seguidores ?? null },
                  { rotulo: "Curtidas", valor: perfilTikTok?.curtidas ?? null },
                  { rotulo: "Vídeos", valor: perfilTikTok?.videos ?? null },
                ]}
              />
            ) : !sociais.isPending ? (
              <RedeVazia>
                Escaneie o QR com o app do TikTok. Na primeira vez ele pode pedir
                senha, e-mail ou SMS.
              </RedeVazia>
            ) : null}
          </CartaoDeRede>
        </div>
      </div>

      <Modal
        aberto={modalPaginasAberto}
        titulo="O que este projeto vai usar?"
        descricao="Pode ser só o Instagram, só a página do Facebook, os dois, ou deixar para escolher depois."
        onFechar={() => {
          definirModalPaginasAberto(false);
          definirBuscaPagina("");
        }}
        mostrarFechar={!salvandoPagina}
        travarTopo={false}
        rodape={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variante="ghost"
              tamanho="sm"
              disabled={salvandoPagina}
              onClick={() => {
                definirModalPaginasAberto(false);
                definirBuscaPagina("");
              }}
            >
              Agora não
            </Button>
            <Button
              type="button"
              variante="brand"
              tamanho="sm"
              disabled={
                salvandoPagina ||
                carregandoPaginas ||
                !paginaEscolhida ||
                paginasFiltradas.length === 0
              }
              onClick={() => void confirmarPagina()}
            >
              {salvandoPagina
                ? "Salvando…"
                : modoVinculoEscolhido === "instagram"
                  ? "Usar só este Instagram"
                  : modoVinculoEscolhido === "pagina"
                    ? "Usar só esta página"
                    : "Usar página e Instagram"}
            </Button>
          </div>
        }
      >
        {carregandoPaginas ? (
          <Esqueleto className="h-28 w-full" />
        ) : (
          <div className="flex flex-col gap-5">
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-foreground">
                Como vincular
              </legend>
              <div className="flex flex-col gap-2">
                {(
                  [
                    {
                      valor: "instagram" as const,
                      rotulo: "Só Instagram",
                      dica: "Para publicar no Instagram deste projeto.",
                    },
                    {
                      valor: "pagina_e_instagram" as const,
                      rotulo: "Página e Instagram",
                      dica: "Os dois ficam disponíveis na agenda.",
                    },
                    {
                      valor: "pagina" as const,
                      rotulo: "Só página do Facebook",
                      dica: "Sem Instagram neste projeto.",
                    },
                  ] as const
                ).map((opcao) => {
                  const marcado = modoVinculoEscolhido === opcao.valor;
                  return (
                    <label
                      key={opcao.valor}
                      className={cn(
                        "flex cursor-pointer gap-3 rounded-lg border px-3 py-3 transition-colors",
                        marcado
                          ? "border-foreground bg-muted/60"
                          : "border-border hover:bg-muted/40",
                      )}
                    >
                      <input
                        type="radio"
                        name="modo-vinculo-meta"
                        className="mt-1"
                        checked={marcado}
                        onChange={() => {
                          definirModoVinculoEscolhido(opcao.valor);
                          if (opcao.valor === "instagram") {
                            const atual = paginas.find(
                              (p) => p.facebookPageId === paginaEscolhida,
                            );
                            if (atual && !atual.instagramPageId) {
                              const primeiraComIg = paginas.find(
                                (p) => p.instagramPageId,
                              );
                              definirPaginaEscolhida(
                                primeiraComIg?.facebookPageId ?? "",
                              );
                            }
                          }
                        }}
                      />
                      <span className="min-w-0 space-y-1">
                        <span className="block text-sm font-medium text-foreground">
                          {opcao.rotulo}
                        </span>
                        <span className="block text-xs leading-relaxed text-muted-foreground">
                          {opcao.dica}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <CampoDeTexto
              rotulo="Buscar"
              placeholder="Nome da página ou @ do Instagram"
              value={buscaPagina}
              onChange={(evento) => definirBuscaPagina(evento.target.value)}
              tamanho="pequeno"
              icone={<Search aria-hidden="true" />}
              autoComplete="off"
            />
            {paginasFiltradas.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border px-4 py-5 text-sm leading-relaxed text-muted-foreground">
                {termoBusca
                  ? "Nada combina com essa busca. Tente outro nome ou @."
                  : modoVinculoEscolhido === "instagram"
                    ? "Nenhuma página desta conta tem Instagram profissional ligado."
                    : "Nenhuma página disponível nesta conta."}
              </p>
            ) : (
              <ul className="flex max-h-[min(16rem,32vh)] flex-col gap-3 overflow-y-auto pr-1">
                {paginasFiltradas.map((pagina) => {
                  const selecionada =
                    pagina.facebookPageId === paginaEscolhida;
                  return (
                    <li key={pagina.facebookPageId}>
                      <button
                        type="button"
                        className={cn(
                          "flex w-full cursor-pointer items-center gap-3 rounded-lg border px-3 py-3 text-left text-sm transition-colors",
                          selecionada
                            ? "border-foreground bg-muted/60"
                            : "border-border hover:bg-muted/40",
                        )}
                        onClick={() =>
                          definirPaginaEscolhida(pagina.facebookPageId)
                        }
                        aria-pressed={selecionada}
                      >
                        <Avatar className="h-10 w-10 shrink-0 overflow-hidden">
                          {pagina.facebookPageFotoUrl ? (
                            <AvatarImage
                              src={pagina.facebookPageFotoUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                          <AvatarFallback className="text-xs">
                            {iniciaisDeNome(pagina.nome)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="min-w-0 flex-1 space-y-1">
                          <span className="block font-medium leading-snug text-foreground">
                            {modoVinculoEscolhido === "instagram" &&
                            pagina.instagramUsername
                              ? `@${pagina.instagramUsername}`
                              : pagina.nome}
                          </span>
                          <span className="flex items-center gap-2 text-xs leading-relaxed text-muted-foreground">
                            {pagina.instagramFotoUrl ? (
                              <Avatar className="h-5 w-5 shrink-0 overflow-hidden">
                                <AvatarImage
                                  src={pagina.instagramFotoUrl}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                                <AvatarFallback className="text-[9px]">
                                  IG
                                </AvatarFallback>
                              </Avatar>
                            ) : null}
                            {modoVinculoEscolhido === "instagram"
                              ? `via ${pagina.nome}`
                              : pagina.instagramUsername
                                ? `@${pagina.instagramUsername}`
                                : "Sem Instagram profissional nesta página"}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {paginaSelecionada &&
        !paginaSelecionada.instagramPageId &&
        modoVinculoEscolhido !== "pagina" ? (
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            Esta página não tem Instagram profissional ligado. Escolha outra ou
            mude para “Só página do Facebook”.
          </p>
        ) : null}
      </Modal>

      <ModalLoginQrTikTok
        aberto={modalQrAberto}
        onFechar={() => definirModalQrAberto(false)}
        onConectada={(username) => {
          void aoConectarTikTokPorQr(username);
        }}
      />
    </div>
  );
}


function rotuloContaSocial(conta: {
  readonly username: string | null;
  readonly nomeExibicao: string | null;
}): string {
  const handle = conta.username?.trim();
  if (handle && !/\s/.test(handle)) {
    return `@${handle.replace(/^@/, "")}`;
  }
  const nome = conta.nomeExibicao?.trim() || handle;
  return nome && nome.length > 0 ? nome : "Conta conectada";
}

function iniciaisDeNome(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase();
  return `${partes[0]![0] ?? ""}${partes[1]![0] ?? ""}`.toUpperCase();
}

function normalizarParaBusca(valor: string): string {
  return valor
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .trim();
}
